import React from "react";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import "./QuotaNotice.css";

const MARKETING_PRICING_URL = "https://packer-ai.com/pricing";

const TIER_LABEL = { free: "Free", core: "Core", pro: "Pro" };

function formatResetDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

/**
 * Shown when /pack returns 402 quota_exceeded. The API sends the whole quota
 * snapshot in `details`, so this renders exact numbers rather than a generic
 * "limit reached" — people are far more likely to upgrade when they can see
 * what they actually used and when it resets.
 */
export default function QuotaNotice({ quota, onDismiss }) {
    if (!quota) return null;
    const { tier, used, limit } = quota;
    const resetsOn = formatResetDate(quota.resets_at);
    const tierLabel = TIER_LABEL[tier] || tier;

    return (
        <div className="quota-notice" role="alert">
            <div className="quota-notice__icon" aria-hidden="true">
                <AlertTriangle size={16} />
            </div>
            <div className="quota-notice__body">
                <p className="quota-notice__title">
                    You've used all {limit} packs on your {tierLabel} plan this month
                </p>
                <p className="quota-notice__detail">
                    {used} of {limit} used
                    {resetsOn ? ` · resets ${resetsOn}` : ""}. Upgrade for a bigger
                    allowance, or come back after the reset.
                </p>
                <div className="quota-notice__actions">
                    <a
                        className="quota-notice__cta"
                        href={MARKETING_PRICING_URL}
                        target="_blank"
                        rel="noreferrer"
                    >
                        See plans
                        <ArrowUpRight size={14} aria-hidden="true" />
                    </a>
                    {onDismiss && (
                        <button
                            type="button"
                            className="quota-notice__dismiss"
                            onClick={onDismiss}
                        >
                            Dismiss
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Compact "12 of 25 packs used" line for the input rail. */
export function QuotaMeter({ quota }) {
    if (!quota || quota.unlimited) return null;
    const { used, limit } = quota;
    if (typeof limit !== "number") return null;

    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
    const tone = pct >= 100 ? "is-spent" : pct >= 80 ? "is-low" : "";

    return (
        <div className={`quota-meter ${tone}`}>
            <div className="quota-meter__row">
                <span className="quota-meter__label">Monthly packs</span>
                <span className="quota-meter__count">
                    {used} / {limit}
                </span>
            </div>
            <div
                className="quota-meter__track"
                role="progressbar"
                aria-valuenow={used}
                aria-valuemin={0}
                aria-valuemax={limit}
                aria-label="Monthly pack allowance used"
            >
                <div className="quota-meter__fill" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
